'use client';
import React, { useEffect, useState, FC } from 'react';

import { useParams } from 'next/navigation';

import STATIC_ROUTES from '@data/staticRoutes.json';
import { useBreakpoint } from '@contexts/breakpoints';
import {
  LoginRedirectModal,
  getLoyaltyTypeFromUtmSource,
  checkLoyaltyPreviouslyValidated,
} from '@components/ui/loyalty-verifications/helpers';
import NotFound from '@components/Errors/404/404';
import { FlexibleDiv, Row } from '@components/formStyles';
import {
  MetaTitle,
  NotFoundWrapper,
} from '@components/views/hotel/listing/HotelListingStyle';
import { FALLBACK_IMAGE_URL, SORT_DATA } from '@constants/constants';
import LoyaltyVerifications from '@components/ui/loyalty-verifications/LoyaltyVerifications';
import { useToggle } from '@hooks/common';
import { useAuthState } from '@contexts/auth/AuthContext';
import { ScrollToTop } from '@shared';
import SortingTabs from '@shared/SortingTabs';
import TourSearchBar from '@domain/tour/searchBar';
import { useSearchParamsToQuery } from '@hooks/useSearchParamsToQuery';
import { useAppRouter } from '@contexts/router';
import Pagination from '@components/Pagination/next/Pagination';
import { useSearchURL } from '@api/data-loaders/resource';
import { TourViewModel } from '@api/index';
import Banner from '@components/views/hotel/listing/components/Banner/Banner';
import { useListingBanner } from '@components/views/hotel/listing/hooks/useListingBanner';

import {
  Wrapper,
  ItemsRow,
  SearchBarWrapper,
  SearchBarContentWrapper,
  CardsWrapper,
  TourCardWrapper,
} from './TourListingStyle';
import ResponsiveFilter from '../components/filters/ResponsiveFilter';
import TourFilters from './TourFilters';
import TourListMobileHeader from './components/TourListMobileHeader';
import { ITourProps } from '.';
import TourCardSkeleton from '../components/ListingSkeleton/TourCardSkeleton';
import TourListingCard from '../cards/TourListingCard/TourListingCard';

const TourListing: FC<ITourProps> = ({
  resourceType,
  initialTourSSRData,
  is404,
}) => {
  const urlData = useSearchURL();
  const query = useSearchParamsToQuery();
  const params = useParams();
  const { push, refresh, isLoading } = useAppRouter();
  const { xl } = useBreakpoint();

  const [sort, setSort] = useState(SORT_DATA[0]);
  const { loginInfo } = useAuthState();

  const { filters, meta, data: tours } = initialTourSSRData || {};

  // Fetch banner data separately
  const { banner } = useListingBanner();

  const isLoyaltyValidated = checkLoyaltyPreviouslyValidated(
    query?.utm_source as string
  );
  const hasUtmSource = !!query?.utm_source;
  const loyaltyType = getLoyaltyTypeFromUtmSource(query?.utm_source as string);
  const isLoggedIn = !!loginInfo;
  const [showLoyaltyModal, setShowLoyaltyModal] = useState<boolean>(
    isLoyaltyValidated ? false : hasUtmSource
  );
  const [showLoginRedirectionModal, , setShowLoginRedirectionModal] =
    useToggle(hasUtmSource);

  // ============================
  // Handle sortBy click
  // ============================
  const handleTabClick = (activeKey: string): void => {
    const item = SORT_DATA.find((item) => item.id === activeKey);
    if (item) {
      setSort(item);
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { ...nextQuery } = query;

      push({
        pathname: `${STATIC_ROUTES.tourPathPrefix}/${params?.slug}`,
        query: { ...nextQuery, sort: item.id },
      });
    }
  };

  // ============================================
  // Fill the sortBy filed when the page load or query updated
  // =================================================
  // ============================================
  // Fill the sortBy filed when the page load or query updated
  // =================================================
  useEffect(() => {
    const activeSort = SORT_DATA.find((item) => item.id === query.sort);

    if (!activeSort) return;
    setSort(activeSort);
  }, [query?.sort]);

  return (
    <>
      {xl ? (
        <SearchBarWrapper>
          <SearchBarContentWrapper>
            <TourSearchBar isListing />
          </SearchBarContentWrapper>
        </SearchBarWrapper>
      ) : (
        <TourListMobileHeader isListing />
      )}
      <ItemsRow>
        <ScrollToTop />

        {xl ? (
          <TourFilters
            applicableFilters={filters}
            listingTitle={`${urlData?.text} ${
              meta?.totalCount ? `(${meta?.totalCount})` : ''
            }`}
          />
        ) : null}
        <Wrapper>
          <FlexibleDiv bottom={xl === undefined || xl ? 21 : -36}>
            {xl === undefined || xl ? (
              <FlexibleDiv top={24} bottom={24}>
                <Row alignItems='center' justifyContent='space-between'>
                  {urlData?.text && meta?.totalCount ? (
                    <Row>
                      <MetaTitle bold>{urlData?.text}:</MetaTitle>
                      <MetaTitle style={{ marginLeft: 3 }}>
                        {meta?.totalCount} tesis bulundu
                      </MetaTitle>
                    </Row>
                  ) : (
                    <div />
                  )}
                  <SortingTabs
                    activeKey={sort.id}
                    data={SORT_DATA}
                    handleTabClick={handleTabClick}
                  />
                </Row>
              </FlexibleDiv>
            ) : null}
            {xl !== undefined && !xl ? (
              <ResponsiveFilter applicableFilters={filters} />
            ) : null}

            {/* Banner Section */}
            {banner && banner.items && banner.items.length > 0 && xl && (
              <Banner items={banner.items} pathPrefix='tour' />
            )}

            {is404 ? (
              <NotFoundWrapper as='section' aria-label='Sonuç Bulunamadı'>
                <NotFound
                  iconSize={124}
                  label={
                    resourceType === 'Tur'
                      ? 'Aradığınız kriterlerde tur bulunamadı, lütfen tekrar deneyin'
                      : 'Aradığınız kriterlere göre Gemi bulunamadı, lütfen tekrar deneyin.'
                  }
                />
              </NotFoundWrapper>
            ) : (
              <section aria-labelledby='tours-list-heading'>
                <CardsWrapper as='ul' aria-label={`${resourceType} kartları`}>
                  {isLoading
                    ? Array(5)
                        .fill(0)
                        .map((_, index) => (
                          <li key={`skeleton-tour-${index}`}>
                            <TourCardSkeleton />
                          </li>
                        ))
                    : tours?.map((tour: TourViewModel, i) => (
                        <TourCardWrapper key={tour.id} as='li'>
                          <TourListingCard
                            tourListing={true}
                            installments={tour.installments}
                            name={tour.name}
                            boardType={tour.tourType}
                            nightCount={tour.nightStay}
                            isTour={true}
                            discountRate={tour.pricing?.rate}
                            discounted={tour.pricing?.discounted}
                            originalPrice={tour.pricing?.original}
                            sellingPrice={tour.pricing?.selling}
                            currency={tour.pricing?.currency}
                            discountType={tour.pricing?.type}
                            discountCurrency={tour.pricing?.currency}
                            milePrice={tour.pricing?.milePrice}
                            secure={tour.secure}
                            departureCityName={tour.departureCityName}
                            features={tour.features || []}
                            placesToGo={tour.placesToGo}
                            nightStay={tour.nightStay}
                            dayStay={tour.dayStay}
                            url={`${STATIC_ROUTES.tourPathPrefix}/${
                              tour?.url || ''
                            }`}
                            transportionTypeName={tour.transportionTypeName}
                            transportionTypeCode={
                              tour.transportionTypeCode || ''
                            }
                            dates={tour.dates || []}
                            images={tour.images || []}
                            fallBackImage={FALLBACK_IMAGE_URL}
                            videos={tour.videos || []}
                            priorityImage={i < 2} // First two images get priority loading to improve LCP
                          />
                        </TourCardWrapper>
                      ))}
                </CardsWrapper>
              </section>
            )}
          </FlexibleDiv>

          {meta?.totalPages && meta?.totalPages > 1 && (
            <nav aria-label='Sayfalama'>
              <Pagination
                totalItemCount={meta?.totalCount}
                currentPage={query.page ? +query.page : 1}
                totalPages={meta?.totalPages}
                type='tour'
                searchTitle={urlData?.text}
              />
            </nav>
          )}
        </Wrapper>

        {showLoyaltyModal && isLoggedIn && loyaltyType ? (
          <LoyaltyVerifications
            action={refresh}
            loyaltyType={loyaltyType}
            onCancel={() => setShowLoyaltyModal(false)}
          />
        ) : null}
        {!isLoggedIn && showLoginRedirectionModal && loyaltyType ? (
          <LoginRedirectModal
            onCancel={() => setShowLoginRedirectionModal(false)}
            loyaltyType={loyaltyType}
          />
        ) : null}
      </ItemsRow>
    </>
  );
};

export default TourListing;
